import React, { useState } from 'react';
import { Factory, FlaskConical, Fuel, Building2, Briefcase, Settings, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Industry {
  name: string;
  icon: LucideIcon;
}

const industries: Industry[] = [
  { name: 'Engineering & Equipment', icon: Settings },
  { name: 'Chemical, Food & Healthcare', icon: FlaskConical },
  { name: 'Energy and Utilities', icon: Fuel },
  { name: 'Trading Houses', icon: Building2 },
  { name: 'Professional Services', icon: Briefcase },
  { name: 'Other Allied Industries', icon: Factory }
];

type DesignVariant = 'pills' | 'icon-strip' | 'floating-cards' | 'minimal-list';

interface IndustryShowcaseProps {
  showSwitcher?: boolean;
  defaultVariant?: DesignVariant;
}

// Pill/Tag Cloud Design
const PillsDesign = () => (
  <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
    {industries.map((industry, index) => (
      <div
        key={index}
        className="inline-flex items-center gap-2.5 px-4 py-2.5 sm:px-5 sm:py-3
                   bg-card rounded-full border border-border/50
                   shadow-sm hover:shadow-md hover:border-primary/30
                   hover:-translate-y-0.5 transition-all duration-200
                   group cursor-default"
        role="listitem"
      >
        <industry.icon 
          className="w-4 h-4 sm:w-5 sm:h-5 text-primary 
                     group-hover:scale-110 transition-transform duration-200" 
          aria-hidden="true" 
        />
        <span className="text-sm sm:text-base font-medium text-foreground">
          {industry.name}
        </span>
      </div>
    ))}
  </div>
);

// Icon Strip Design
const IconStripDesign = () => (
  <div className="flex flex-wrap justify-center gap-6 sm:gap-10 lg:gap-12">
    {industries.map((industry, index) => (
      <div
        key={index}
        className="flex flex-col items-center gap-3 group cursor-default
                   hover:-translate-y-1 transition-all duration-200"
        role="listitem"
      >
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-card border border-border/50
                        flex items-center justify-center shadow-sm
                        group-hover:shadow-md group-hover:border-primary/30
                        group-hover:bg-primary/5 transition-all duration-200">
          <industry.icon 
            className="w-7 h-7 sm:w-8 sm:h-8 text-primary
                       group-hover:scale-110 transition-transform duration-200" 
            aria-hidden="true" 
          />
        </div>
        <span className="text-xs sm:text-sm font-medium text-muted-foreground text-center
                         group-hover:text-foreground transition-colors duration-200 max-w-[100px]">
          {industry.name}
        </span>
      </div>
    ))}
  </div>
);

// Floating Cards Design
const FloatingCardsDesign = () => (
  <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
    {industries.map((industry, index) => (
      <div
        key={index}
        className={cn(
          "flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4",
          "bg-card rounded-xl border border-border/30",
          "shadow-md hover:shadow-lg transition-all duration-300",
          "group cursor-default",
          // Staggered effect on hover
          "hover:-translate-y-1 hover:rotate-1",
          // Alternating subtle tilt
          index % 2 === 0 ? "hover:-rotate-1" : "hover:rotate-1"
        )}
        role="listitem"
      >
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-primary/10 
                        flex items-center justify-center
                        group-hover:bg-primary/20 transition-colors duration-200">
          <industry.icon 
            className="w-5 h-5 sm:w-6 sm:h-6 text-primary
                       group-hover:scale-110 transition-transform duration-200" 
            aria-hidden="true" 
          />
        </div>
        <span className="text-sm sm:text-base font-medium text-foreground">
          {industry.name}
        </span>
      </div>
    ))}
  </div>
);

// Minimal List Design
const MinimalListDesign = () => (
  <div className="max-w-2xl mx-auto">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      {industries.map((industry, index) => (
        <div
          key={index}
          className="flex items-center gap-3 px-4 py-3 rounded-lg
                     hover:bg-card/50 transition-all duration-200
                     group cursor-default border-b border-border/20 last:border-b-0
                     sm:border-b-0 sm:border-l-2 sm:border-l-transparent
                     sm:hover:border-l-primary"
          role="listitem"
        >
          <industry.icon 
            className="w-5 h-5 text-primary shrink-0
                       group-hover:scale-110 transition-transform duration-200" 
            aria-hidden="true" 
          />
          <span className="text-sm sm:text-base font-medium text-foreground">
            {industry.name}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const variantComponents: Record<DesignVariant, React.FC> = {
  'pills': PillsDesign,
  'icon-strip': IconStripDesign,
  'floating-cards': FloatingCardsDesign,
  'minimal-list': MinimalListDesign,
};

const variantLabels: Record<DesignVariant, string> = {
  'pills': 'Pills',
  'icon-strip': 'Icons',
  'floating-cards': 'Cards',
  'minimal-list': 'List',
};

export const IndustryShowcase: React.FC<IndustryShowcaseProps> = ({ 
  showSwitcher = true,
  defaultVariant = 'pills' 
}) => {
  const [variant, setVariant] = useState<DesignVariant>(defaultVariant);
  const DesignComponent = variantComponents[variant];

  return (
    <div className="space-y-8">
      {/* Variant Switcher - for testing different designs */}
      {showSwitcher && (
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1 p-1 bg-muted/50 rounded-full border border-border/50">
            {(Object.keys(variantComponents) as DesignVariant[]).map((v) => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                className={cn(
                  "px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-all duration-200",
                  variant === v 
                    ? "bg-primary text-primary-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {variantLabels[v]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected Design */}
      <div role="list" aria-label="Industries we serve">
        <DesignComponent />
      </div>
    </div>
  );
};

export default IndustryShowcase;
